/* tslint:disable */
/* eslint-disable */
/**
 * Distributor API
 * The Cashlink Distributor API enables you to distribute crypto securities and digital securities. It contains all building blocks from investor registration and KYC/AML to brokerage, clearing and settlement processes. While you are in full control of the frontends served to your investors you can use the Cashlink Studio to monitor and manage the activity on your platform.   Note: You cannot access data of investors or investments if they haven\'t been created via the API with your provided API credentials. Also, it\'s not possible to access data of investors that have registered via the Cashlink Signing Module, if you are using this as well.  ### Authentication  Every call to the API must be authenticated via OAuth client credentials. During setup, your point of contact will provide you with your client ID and client secret.   ### Idempotency  For certain POST endpoints, the API uses client-supplied idempotency keys to ensure that requests can be retried safely without performing the same operation twice. Repeated calls to those endpoints with the same idempotency key will return the cached response from the first call. Keys are expected to be set as header parameters with *X-Idempotency-Key*. The recommended approach for interacting with such endpoints is as follows:   - Create a unique, random key, such as a UUIDv4.   - Store the intent to call an endpoint along with the generated key in the client database.   - Flush to disk (e.g. commit an SQL transaction).   - Perform the call to the endpoint.   This ensures that even if the response from the API to a client gets lost, or the client experiences any sort of outage between steps 3 and 4, the client can always safely retry the same action.   If you already have objects corresponding to tasks in the API in your client-side database, you can use their internal IDs as idempotency keys!   ### Monetary values   Monetary values are represented as a combination of amount, decimals & currency. For example, ```{\'amount\': \'40000000\', \'decimals\': 3, \'currency\': \'EUR\'} means \'EUR 40,000.000\'```.   ### Handling documents  The document endpoint can be used to upload documents. Files are uploaded via HTTP multipart. The endpoint will return a document ID that can be used in other requests to refer to that document.   ### Webhooks  You can also register webhooks so that we can notify your application about events with regards to investments. To use that, please provide your point of contact at Cashlink with the following data:   - URL to be used as the target for webhooks  - OAuth client credentials or bearer authentication token so that we can authenticate at your server.  Webhooks are sent every time the status of an investment changes.  **POST <customer-provided URL>**  Webhooks are retried until the called endpoint returns a 2xx.  **Request**  {  \'investment_id\': \'3fa85f64-5717-4562-b3fc-2c963f66afa6\',  \'status\': \'ACCEPTED\',  \'changed_at\': \'2020-12-11T14:59:46.716Z\'  }  ### Concepts  The Cashlink Distributor API is based on the following concepts:  - Persons (legal or natural) that are users on your platform willing to invest are called investors.  - These investors will be able to invest into different financial products issued by different entities. In the API they are called products.  - The purchase of units of such a product takes place within your application and ends with a signed investment contract.  - Now you can create an investment, which contains the investor, the units, the signed investment contract and the accepted date of the investment by the issuer.   - At some point, parts of the investments are returned to the issuer because the outstanding amount has been paid back for example. Such a transaction is called a redemption.    
 *
 * The version of the OpenAPI document: v2
 * Contact: support@cashlink.de
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

import { mapValues } from '../runtime';
import type { Money } from './Money';
import {
    MoneyFromJSON,
    MoneyFromJSONTyped,
    MoneyToJSON,
} from './Money';
import type { ProductTypeEnum } from './ProductTypeEnum';
import {
    ProductTypeEnumFromJSON,
    ProductTypeEnumFromJSONTyped,
    ProductTypeEnumToJSON,
} from './ProductTypeEnum';
import type { CustomCommitmentTextEnum } from './CustomCommitmentTextEnum';
import {
    CustomCommitmentTextEnumFromJSON,
    CustomCommitmentTextEnumFromJSONTyped,
    CustomCommitmentTextEnumToJSON,
} from './CustomCommitmentTextEnum';
import type { Issuer } from './Issuer';
import {
    IssuerFromJSON,
    IssuerFromJSONTyped,
    IssuerToJSON,
} from './Issuer';
import type { ProductPermissionEnum } from './ProductPermissionEnum';
import {
    ProductPermissionEnumFromJSON,
    ProductPermissionEnumFromJSONTyped,
    ProductPermissionEnumToJSON,
} from './ProductPermissionEnum';
import type { TokenContractInfo } from './TokenContractInfo';
import {
    TokenContractInfoFromJSON,
    TokenContractInfoFromJSONTyped,
    TokenContractInfoToJSON,
} from './TokenContractInfo';
import type { CustomCheckbox } from './CustomCheckbox';
import {
    CustomCheckboxFromJSON,
    CustomCheckboxFromJSONTyped,
    CustomCheckboxToJSON,
} from './CustomCheckbox';
import type { VersionedIssuanceDocument } from './VersionedIssuanceDocument';
import {
    VersionedIssuanceDocumentFromJSON,
    VersionedIssuanceDocumentFromJSONTyped,
    VersionedIssuanceDocumentToJSON,
} from './VersionedIssuanceDocument';
import type { Document } from './Document';
import {
    DocumentFromJSON,
    DocumentFromJSONTyped,
    DocumentToJSON,
} from './Document';

/**
 * view of the token for investor
 * @export
 * @interface InvestmentTokenForInvestor
 */
export interface InvestmentTokenForInvestor {
    /**
     * 
     * @type {string}
     * @memberof InvestmentTokenForInvestor
     */
    readonly id: string;
    /**
     * Defines whether commitment requires a bank account for investor
     * @type {boolean}
     * @memberof InvestmentTokenForInvestor
     */
    readonly bankAccountRequired: boolean;
    /**
     * Shall accrued interests be calculated for investments and shown in payment details?
     * @type {boolean}
     * @memberof InvestmentTokenForInvestor
     */
    readonly calculateAccruedInterest: boolean;
    /**
     * This is a product with an eWpG issuance. That means we need to create registry statements
     * @type {boolean}
     * @memberof InvestmentTokenForInvestor
     */
    readonly ewpg: boolean;
    /**
     * Defines whether this token complies with ECSP.
     * @type {boolean}
     * @memberof InvestmentTokenForInvestor
     */
    readonly isEcsp: boolean;
    /**
     * ISIN of security
     * @type {string}
     * @memberof InvestmentTokenForInvestor
     */
    readonly isin: string | null;
    /**
     * 
     * @type {VersionedIssuanceDocument}
     * @memberof InvestmentTokenForInvestor
     */
    readonly issuanceDocument: VersionedIssuanceDocument;
    /**
     * 
     * @type {Issuer}
     * @memberof InvestmentTokenForInvestor
     */
    issuer: Issuer;
    /**
     * 
     * @type {boolean}
     * @memberof InvestmentTokenForInvestor
     */
    readonly managed: boolean;
    /**
     * 
     * @type {string}
     * @memberof InvestmentTokenForInvestor
     */
    readonly name: string;
    /**
     * 
     * @type {Money}
     * @memberof InvestmentTokenForInvestor
     */
    nominalValue: Money;
    /**
     * 
     * @type {Array<ProductPermissionEnum>}
     * @memberof InvestmentTokenForInvestor
     */
    readonly permissions: Array<ProductPermissionEnum>;
    /**
     * Type of financial product
     * @type {ProductTypeEnum}
     * @memberof InvestmentTokenForInvestor
     */
    readonly productType: ProductTypeEnum;
    /**
     * Indicates whether the most recent publication is valid. A publication turns invalid if no investment was made during the first 3 months after the creation of the crypto security
     * @type {boolean}
     * @memberof InvestmentTokenForInvestor
     */
    readonly publicationValid: boolean | null;
    /**
     * the number of units that are untouched, i.e. neither used in invitations, fulfilled nor issued
     * @type {number}
     * @memberof InvestmentTokenForInvestor
     */
    readonly remainingAmount: number;
    /**
     * Defines whether commitment requires a securities depot for investor
     * @type {boolean}
     * @memberof InvestmentTokenForInvestor
     */
    readonly securitiesDepositAccountRequired: boolean;
    /**
     * 
     * @type {string}
     * @memberof InvestmentTokenForInvestor
     */
    readonly tokenType: string;
    /**
     * 
     * @type {TokenContractInfo}
     * @memberof InvestmentTokenForInvestor
     */
    tokenizationInfo: TokenContractInfo | null;
    /**
     * 
     * @type {Document}
     * @memberof InvestmentTokenForInvestor
     */
    vib: Document;
    /**
     * URL to the product's website
     * @type {string}
     * @memberof InvestmentTokenForInvestor
     */
    readonly website: string | null;
    /**
     * 
     * @type {boolean}
     * @memberof InvestmentTokenForInvestor
     */
    readonly canUseTanganyWallet: boolean;
    /**
     * 
     * @type {boolean}
     * @memberof InvestmentTokenForInvestor
     */
    readonly canUseCashlinkWallet: boolean;
    /**
     * 
     * @type {boolean}
     * @memberof InvestmentTokenForInvestor
     */
    readonly canUseMobileWallet: boolean;
    /**
     * 
     * @type {boolean}
     * @memberof InvestmentTokenForInvestor
     */
    readonly selfDisclosureRequired: boolean;
    /**
     * 
     * @type {Array<CustomCheckbox>}
     * @memberof InvestmentTokenForInvestor
     */
    readonly customCheckboxes: Array<CustomCheckbox>;
    /**
     * 
     * @type {Document}
     * @memberof InvestmentTokenForInvestor
     */
    readonly productImage: Document | null;
    /**
     * 
     * @type {string}
     * @memberof InvestmentTokenForInvestor
     */
    readonly unitSteps: string | null;
    /**
     * 
     * @type {boolean}
     * @memberof InvestmentTokenForInvestor
     */
    readonly useUnits: boolean | null;
    /**
     * 
     * @type {CustomCommitmentTextEnum}
     * @memberof InvestmentTokenForInvestor
     */
    readonly customCommitmentText: CustomCommitmentTextEnum | null;
    /**
     * 
     * @type {Document}
     * @memberof InvestmentTokenForInvestor
     */
    readonly customCommitmentDoc: Document | null;
}



/**
 * Check if a given object implements the InvestmentTokenForInvestor interface.
 */
export function instanceOfInvestmentTokenForInvestor(value: object): value is InvestmentTokenForInvestor {
    if (!('id' in value) || value['id'] === undefined) return false;
    if (!('bankAccountRequired' in value) || value['bankAccountRequired'] === undefined) return false;
    if (!('calculateAccruedInterest' in value) || value['calculateAccruedInterest'] === undefined) return false;
    if (!('ewpg' in value) || value['ewpg'] === undefined) return false;
    if (!('isEcsp' in value) || value['isEcsp'] === undefined) return false;
    if (!('isin' in value) || value['isin'] === undefined) return false;
    if (!('issuanceDocument' in value) || value['issuanceDocument'] === undefined) return false;
    if (!('issuer' in value) || value['issuer'] === undefined) return false;
    if (!('managed' in value) || value['managed'] === undefined) return false;
    if (!('name' in value) || value['name'] === undefined) return false;
    if (!('nominalValue' in value) || value['nominalValue'] === undefined) return false;
    if (!('permissions' in value) || value['permissions'] === undefined) return false;
    if (!('productType' in value) || value['productType'] === undefined) return false;
    if (!('publicationValid' in value) || value['publicationValid'] === undefined) return false;
    if (!('remainingAmount' in value) || value['remainingAmount'] === undefined) return false;
    if (!('securitiesDepositAccountRequired' in value) || value['securitiesDepositAccountRequired'] === undefined) return false;
    if (!('tokenType' in value) || value['tokenType'] === undefined) return false;
    if (!('tokenizationInfo' in value) || value['tokenizationInfo'] === undefined) return false;
    if (!('vib' in value) || value['vib'] === undefined) return false;
    if (!('website' in value) || value['website'] === undefined) return false;
    if (!('canUseTanganyWallet' in value) || value['canUseTanganyWallet'] === undefined) return false;
    if (!('canUseCashlinkWallet' in value) || value['canUseCashlinkWallet'] === undefined) return false;
    if (!('canUseMobileWallet' in value) || value['canUseMobileWallet'] === undefined) return false;
    if (!('selfDisclosureRequired' in value) || value['selfDisclosureRequired'] === undefined) return false;
    if (!('customCheckboxes' in value) || value['customCheckboxes'] === undefined) return false;
    if (!('productImage' in value) || value['productImage'] === undefined) return false;
    if (!('unitSteps' in value) || value['unitSteps'] === undefined) return false;
    if (!('useUnits' in value) || value['useUnits'] === undefined) return false;
    if (!('customCommitmentText' in value) || value['customCommitmentText'] === undefined) return false;
    if (!('customCommitmentDoc' in value) || value['customCommitmentDoc'] === undefined) return false;
    return true;
}

export function InvestmentTokenForInvestorFromJSON(json: any): InvestmentTokenForInvestor {
    return InvestmentTokenForInvestorFromJSONTyped(json, false);
}

export function InvestmentTokenForInvestorFromJSONTyped(json: any, ignoreDiscriminator: boolean): InvestmentTokenForInvestor {
    if (json == null) {
        return json;
    }
    return {
        
        'id': json['id'],
        'bankAccountRequired': json['bank_account_required'],
        'calculateAccruedInterest': json['calculate_accrued_interest'],
        'ewpg': json['ewpg'],
        'isEcsp': json['is_ecsp'],
        'isin': json['isin'],
        'issuanceDocument': VersionedIssuanceDocumentFromJSON(json['issuance_document']),
        'issuer': IssuerFromJSON(json['issuer']),
        'managed': json['managed'],
        'name': json['name'],
        'nominalValue': MoneyFromJSON(json['nominal_value']),
        'permissions': ((json['permissions'] as Array<any>).map(ProductPermissionEnumFromJSON)),
        'productType': ProductTypeEnumFromJSON(json['product_type']),
        'publicationValid': json['publication_valid'],
        'remainingAmount': json['remaining_amount'],
        'securitiesDepositAccountRequired': json['securities_deposit_account_required'],
        'tokenType': json['token_type'],
        'tokenizationInfo': TokenContractInfoFromJSON(json['tokenization_info']),
        'vib': DocumentFromJSON(json['vib']),
        'website': json['website'],
        'canUseTanganyWallet': json['can_use_tangany_wallet'],
        'canUseCashlinkWallet': json['can_use_cashlink_wallet'],
        'canUseMobileWallet': json['can_use_mobile_wallet'],
        'selfDisclosureRequired': json['self_disclosure_required'],
        'customCheckboxes': ((json['custom_checkboxes'] as Array<any>).map(CustomCheckboxFromJSON)),
        'productImage': DocumentFromJSON(json['product_image']),
        'unitSteps': json['unit_steps'],
        'useUnits': json['use_units'],
        'customCommitmentText': CustomCommitmentTextEnumFromJSON(json['custom_commitment_text']),
        'customCommitmentDoc': DocumentFromJSON(json['custom_commitment_doc']),
    };
}

export function InvestmentTokenForInvestorToJSON(value?: Omit<InvestmentTokenForInvestor, 'id'|'bank_account_required'|'calculate_accrued_interest'|'ewpg'|'is_ecsp'|'isin'|'issuance_document'|'managed'|'name'|'permissions'|'product_type'|'publication_valid'|'remaining_amount'|'securities_deposit_account_required'|'token_type'|'website'|'can_use_tangany_wallet'|'can_use_cashlink_wallet'|'can_use_mobile_wallet'|'self_disclosure_required'|'custom_checkboxes'|'product_image'|'unit_steps'|'use_units'|'custom_commitment_text'|'custom_commitment_doc'> | null): any {
    if (value == null) {
        return value;
    }
    return {
        
        'issuer': IssuerToJSON(value['issuer']),
        'nominal_value': MoneyToJSON(value['nominalValue']),
        'tokenization_info': TokenContractInfoToJSON(value['tokenizationInfo']),
        'vib': DocumentToJSON(value['vib']),
    };
}

